import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export interface PedagogyTeacherAnalyticsRow {
  teacherId: string
  fullName: string
  status: 'working' | 'candidate'
  partnerNumber: number
  cityId: number
  cityName: string
  classesCount: number | null
  specialClassesCount: number | null
  attendanceUnits: number | null
  averageAttendance: number | null
  favouriteDirection: string | null
  hasMatchedData: boolean
}

export interface PedagogyTeacherSpecialSessionCount {
  teacherId: string
  specialClassesCount: number
}

function numberOrNull(value: unknown): number | null {
  return value == null ? null : Number(value)
}

export async function listPedagogyTeacherAnalytics(input: {
  periodId: number
  weekNumber?: number | null
  partnerNumber: number
  cityId: number
  teacherId?: string | null
}): Promise<PedagogyTeacherAnalyticsRow[]> {
  // Ordinary teachers open the personal screen without a route parameter.
  // React Router represents that missing parameter as an empty string; the
  // UUID RPC must receive NULL so it can resolve the linked teacher itself.
  const teacherId = input.teacherId?.trim() || null
  const { data, error } = await withAuthRecovery(() => getSupabase().rpc('list_pedagogy_teacher_analytics' as never, {
    p_period_id: input.periodId,
    p_week_number: input.weekNumber ?? null,
    p_partner_number: input.partnerNumber,
    p_city_id: input.cityId,
    p_teacher_id: teacherId,
  } as never))

  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    teacherId: String(row.teacher_id),
    fullName: String(row.full_name ?? ''),
    status: row.status === 'candidate' ? 'candidate' : 'working',
    partnerNumber: Number(row.partner_number),
    cityId: Number(row.city_id),
    cityName: String(row.city_name ?? ''),
    classesCount: numberOrNull(row.classes_count),
    specialClassesCount: 0,
    attendanceUnits: numberOrNull(row.attendance_units),
    averageAttendance: numberOrNull(row.average_attendance),
    favouriteDirection: row.favourite_direction == null ? null : String(row.favourite_direction),
    hasMatchedData: Boolean(row.has_matched_data),
  }))
}

export async function listPedagogyTeacherSpecialSessionCounts(input: {
  periodId: number
  weekNumber?: number | null
  partnerNumber: number
  cityId: number
  teacherId?: string | null
}): Promise<PedagogyTeacherSpecialSessionCount[]> {
  const teacherId = input.teacherId?.trim() || null
  const { data, error } = await withAuthRecovery(() => getSupabase().rpc('list_pedagogy_teacher_special_session_counts' as never, {
    p_period_id: input.periodId,
    p_week_number: input.weekNumber ?? null,
    p_partner_number: input.partnerNumber,
    p_city_id: input.cityId,
    p_teacher_id: teacherId,
  } as never))

  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    teacherId: String(row.teacher_id),
    specialClassesCount: Number(row.special_classes_count ?? 0),
  }))
}
